import {useCallback, useEffect, useRef} from 'react';

import {beginPerfWindowSession} from '../../../util/datadog/criticalWorkflows/perfWindow/observer';
import type {PerfWindowSessionHandle} from '../../../util/datadog/criticalWorkflows/perfWindow/types';

export type LinePlotRenderer = 'react-vis' | 'uplot';

export interface LinePlotHoverMetricContext {
  metricCount: number;
  metricName?: string;
}

interface LinePlotMetricLine {
  aux?: boolean;
  metricName?: string;
}

export const LINE_PLOT_HOVER_MAX_DURATION_MS = 10_000;

const LINE_PLOT_HOVER_WORKFLOW = 'line-plot-hovered';

export function getLinePlotHoverMetricContext(
  lines: readonly LinePlotMetricLine[]
): LinePlotHoverMetricContext {
  const metricNames = new Set<string>();
  for (const line of lines) {
    if (!line.aux && line.metricName != null) {
      metricNames.add(line.metricName);
    }
  }

  if (metricNames.size !== 1) {
    return {metricCount: metricNames.size};
  }
  return {metricCount: 1, metricName: metricNames.values().next().value};
}

// Measures blocking while the pointer is over a uPlot chart. The monitor handles
// the time limit, waits for the last frame, and records whether the tab was hidden.
// This hook starts/stops measurement; its reported duration can extend past mouse leave.
export function useLinePlotHoverSession(
  renderer: LinePlotRenderer,
  metricContext: LinePlotHoverMetricContext
): {onMouseEnter: () => void; onMouseLeave: () => void} {
  const perfObservationRef = useRef<PerfWindowSessionHandle | undefined>(
    undefined
  );
  const rendererRef = useRef(renderer);

  const cancelCurrentSession = useCallback(() => {
    const observation = perfObservationRef.current;
    perfObservationRef.current = undefined;
    observation?.cancel();
  }, []);

  const onMouseEnter = useCallback(() => {
    cancelCurrentSession();
    if (renderer !== 'uplot') {
      return;
    }
    perfObservationRef.current = beginPerfWindowSession({
      trigger: 'workflow',
      workflow: LINE_PLOT_HOVER_WORKFLOW,
      workflowContext: {
        renderer,
        metricCount: metricContext.metricCount,
        ...(metricContext.metricName == null
          ? {}
          : {metricName: metricContext.metricName}),
      },
      maxDurationMs: LINE_PLOT_HOVER_MAX_DURATION_MS,
      minStartIntervalMs: 5_000,
    });
  }, [cancelCurrentSession, metricContext, renderer]);

  // Hiding the tab only sets a flag; the monitor reports data on unload/freeze.
  // complete() does nothing if completion or timeout has already started.
  const onMouseLeave = useCallback(() => {
    const observation = perfObservationRef.current;
    perfObservationRef.current = undefined;
    observation?.complete();
  }, []);

  useEffect(() => {
    if (rendererRef.current !== renderer) {
      cancelCurrentSession();
      rendererRef.current = renderer;
    }
  }, [cancelCurrentSession, renderer]);

  useEffect(() => cancelCurrentSession, [cancelCurrentSession]);

  return {onMouseEnter, onMouseLeave};
}
